import { digitAt, geometry, isGridShape, popcount, type SudokuSize } from "./grid";

/**
 * THE GAME STATE AND ITS MOVES — pure functions, no clock, no I/O. The phone plays with them; the
 * api checks a saved state with {@link validateState} before it stores one.
 *
 * A wrong digit is simply one that differs from the solution (the phone has it, so a red mark needs
 * no request). A hint fills one cell with its answer and LOCKS it like a given — undo never takes it
 * back and the counter never refunds it.
 *
 * MISTAKE CHECKING (the operator, 2026-11-04) — each game is played in ONE {@link SudokuMode}, chosen
 * before it starts (the phone's setting) and kept in its state for good:
 * - "check": a wrong digit is red at once and COUNTED — every wrong digit put into a cell, a wrong one
 *   changed to another wrong one too; the same digit again is no move at all; undo never refunds;
 *   notes and hints never count. The {@link SUDOKU_MAX_MISTAKES}th ends the game ({@link isLost}).
 * - "free": nothing tells right from wrong. The first time the grid is full with mistakes, the screen
 *   says HOW MANY cells are wrong (never where) — once per game ({@link fullGridMistakes},
 *   `fullShown`). From then on «نشانم بده» may mark the wrong cells red, ONCE per game
 *   ({@link reveal}); a marked cell turns neutral again when its number changes. A game it was used
 *   in is «assisted».
 * In both, the grid showing the solution is the win.
 */

/** Mistakes a "check" game survives: this many ends it. */
export const SUDOKU_MAX_MISTAKES = 3;

/** How a game treats mistakes — fixed when it starts. */
export const SUDOKU_MODES = ["check", "free"] as const;
export type SudokuMode = (typeof SUDOKU_MODES)[number];

/** Hints per game. */
export const SUDOKU_HINTS = 3;

/** Moves undo remembers (the oldest drop off); the history is saved with the game, so undo works on
 *  another phone too. */
export const SUDOKU_UNDO_LIMIT = 100;

/** One undo step: the cell, and its entry and notes BEFORE the move. */
export type UndoStep = readonly [cell: number, entry: number, notes: number];

export interface SudokuState {
  /** The player's digits, row by row: `"0"` = none, and always `"0"` on a given. */
  readonly entries: string;
  /** Per cell, the pencil marks: bit `d − 1` = digit `d`. Always 0 on a cell holding a digit. */
  readonly notes: readonly number[];
  /** The cells hints filled, in order — locked like givens; the count is the hints used. */
  readonly hinted: readonly number[];
  /** Play time so far — counted only while the game is on screen. */
  readonly elapsedMs: number;
  /** The latest moves, oldest first, at most {@link SUDOKU_UNDO_LIMIT}. */
  readonly undo: readonly UndoStep[];
  /** How this game treats mistakes (never changes). */
  readonly mode: SudokuMode;
  /** "check": the wrong digits entered so far (0 in "free"). */
  readonly mistakes: number;
  /** "free": the full-grid message was shown (it is shown once per game). */
  readonly fullShown: boolean;
  /** "free": «نشانم بده» was used (once per game — the game is «assisted»). */
  readonly revealed: boolean;
  /** "free": the cells «نشانم بده» marked red that have not changed since. */
  readonly marked: readonly number[];
}

/** A puzzle as the game plays it: its size, its givens and its one solution. */
export interface SudokuBoard {
  readonly size: SudokuSize;
  readonly puzzle: string;
  readonly solution: string;
}

export function newState(size: SudokuSize, mode: SudokuMode = "check"): SudokuState {
  const cells = size * size;
  return {
    entries: "0".repeat(cells),
    notes: new Array<number>(cells).fill(0),
    hinted: [],
    elapsedMs: 0,
    undo: [],
    mode,
    mistakes: 0,
    fullShown: false,
    revealed: false,
    marked: [],
  };
}

export function isGiven(board: SudokuBoard, cell: number): boolean {
  return digitAt(board.puzzle, cell) !== 0;
}

/** A given or a hinted cell: nothing the player does changes it. */
export function isLocked(board: SudokuBoard, state: SudokuState, cell: number): boolean {
  return isGiven(board, cell) || state.hinted.includes(cell);
}

/** The digit shown in a cell: its given, or the player's entry (0 = empty). */
export function shownDigit(board: SudokuBoard, state: SudokuState, cell: number): number {
  return digitAt(board.puzzle, cell) || digitAt(state.entries, cell);
}

/** The player put a digit here that is not the solution's (a given never is). */
export function isWrong(board: SudokuBoard, state: SudokuState, cell: number): boolean {
  const entry = digitAt(state.entries, cell);
  return entry !== 0 && entry !== digitAt(board.solution, cell);
}

/** Every cell shows its solution digit. */
export function isSolved(board: SudokuBoard, state: SudokuState): boolean {
  for (let i = 0; i < board.solution.length; i++) {
    if (shownDigit(board, state, i) !== digitAt(board.solution, i)) return false;
  }
  return true;
}

/** The finished grid as the api checks it: givens and entries together. */
export function filledGrid(board: SudokuBoard, state: SudokuState): string {
  let out = "";
  for (let i = 0; i < board.puzzle.length; i++) out += String(shownDigit(board, state, i));
  return out;
}

export function hintsLeft(state: SudokuState): number {
  return Math.max(0, SUDOKU_HINTS - state.hinted.length);
}

/** How many cells show digit `d` (givens and entries, right or wrong). */
export function digitCount(board: SudokuBoard, state: SudokuState, d: number): number {
  let n = 0;
  for (let i = 0; i < board.puzzle.length; i++) if (shownDigit(board, state, i) === d) n++;
  return n;
}

function withCell(
  state: SudokuState,
  cell: number,
  entry: number,
  notes: number,
  record: boolean,
): SudokuState {
  const entries = state.entries.slice(0, cell) + String(entry) + state.entries.slice(cell + 1);
  const nextNotes = [...state.notes];
  nextNotes[cell] = notes;
  let undo = state.undo;
  if (record) {
    const step: UndoStep = [cell, digitAt(state.entries, cell), state.notes[cell] ?? 0];
    undo = [...state.undo, step];
    if (undo.length > SUDOKU_UNDO_LIMIT) undo = undo.slice(undo.length - SUDOKU_UNDO_LIMIT);
  }
  // A marked cell whose number changes (a digit, erase, undo, a hint) is neutral again.
  const marked = state.marked.includes(cell)
    ? state.marked.filter((c) => c !== cell)
    : state.marked;
  return { ...state, entries, notes: nextNotes, undo, marked };
}

function inRange(board: SudokuBoard, cell: number): boolean {
  return Number.isInteger(cell) && cell >= 0 && cell < board.puzzle.length;
}

/** Put digit `d` in a cell (its notes go). No change on a locked cell or the same digit again. */
export function placeDigit(
  board: SudokuBoard,
  state: SudokuState,
  cell: number,
  d: number,
): SudokuState {
  if (!inRange(board, cell) || !Number.isInteger(d) || d < 1 || d > board.size) return state;
  if (isLocked(board, state, cell) || digitAt(state.entries, cell) === d) return state;
  const next = withCell(state, cell, d, 0, true);
  // "check": every wrong digit put in is a mistake (the same digit again never got here).
  if (state.mode === "check" && d !== digitAt(board.solution, cell)) {
    return { ...next, mistakes: state.mistakes + 1 };
  }
  return next;
}

/** A "check" game that reached {@link SUDOKU_MAX_MISTAKES}: it is over, lost. */
export function isLost(state: SudokuState): boolean {
  return state.mode === "check" && state.mistakes >= SUDOKU_MAX_MISTAKES;
}

/** Every cell shows a digit (right or wrong). */
export function isFull(board: SudokuBoard, state: SudokuState): boolean {
  for (let i = 0; i < board.puzzle.length; i++) if (shownDigit(board, state, i) === 0) return false;
  return true;
}

/** The cells holding a wrong digit, in reading order. */
export function wrongCells(board: SudokuBoard, state: SudokuState): number[] {
  const out: number[] = [];
  for (let i = 0; i < board.puzzle.length; i++) if (isWrong(board, state, i)) out.push(i);
  return out;
}

/**
 * "free": the grid is full with mistakes and the message has not been shown in this game — how many
 * cells are wrong (the message says how many, never where); 0 when there is nothing to say.
 */
export function fullGridMistakes(board: SudokuBoard, state: SudokuState): number {
  if (state.mode !== "free" || state.fullShown || !isFull(board, state)) return 0;
  return wrongCells(board, state).length;
}

/** «نشانم بده» may be used: a "free" game, after the full-grid message, not used yet. */
export function canReveal(state: SudokuState): boolean {
  return state.mode === "free" && state.fullShown && !state.revealed;
}

/** «نشانم بده»: the wrong cells turn red (until each changes) and the game is «assisted». Once — and
 *  whatever the board holds: with no cell wrong it marks nothing and is spent all the same (never
 *  refusing it is what keeps the count from showing again — the operator, 2026-11-04). */
export function reveal(board: SudokuBoard, state: SudokuState): SudokuState {
  if (!canReveal(state)) return state;
  return { ...state, revealed: true, marked: wrongCells(board, state) };
}

/**
 * `next` can follow `prev` in one game: the same mode, no mistake refunded, neither the message nor
 * «نشانم بده» taken back. The api checks it on every save — else a save could clear «assisted».
 */
export function continuesFrom(prev: SudokuState, next: SudokuState): boolean {
  return (
    next.mode === prev.mode &&
    next.mistakes >= prev.mistakes &&
    (next.fullShown || !prev.fullShown) &&
    (next.revealed || !prev.revealed)
  );
}

/** Flip pencil mark `d` in an EMPTY cell (a cell holding a digit takes no notes). */
export function toggleNote(
  board: SudokuBoard,
  state: SudokuState,
  cell: number,
  d: number,
): SudokuState {
  if (!inRange(board, cell) || !Number.isInteger(d) || d < 1 || d > board.size) return state;
  if (isLocked(board, state, cell) || digitAt(state.entries, cell) !== 0) return state;
  return withCell(state, cell, 0, (state.notes[cell] ?? 0) ^ (1 << (d - 1)), true);
}

/** Clear a cell's digit and notes. No change on a locked cell or one already empty. */
export function eraseCell(board: SudokuBoard, state: SudokuState, cell: number): SudokuState {
  if (!inRange(board, cell) || isLocked(board, state, cell)) return state;
  if (digitAt(state.entries, cell) === 0 && (state.notes[cell] ?? 0) === 0) return state;
  return withCell(state, cell, 0, 0, true);
}

/** Take back the latest move. A step on a cell a hint has since locked is dropped, not undone. */
export function undoMove(board: SudokuBoard, state: SudokuState): SudokuState {
  const undo = [...state.undo];
  while (undo.length > 0) {
    const step = undo.pop() as UndoStep;
    const [cell, entry, notes] = step;
    if (!inRange(board, cell) || isLocked(board, state, cell)) continue;
    const next = withCell({ ...state, undo }, cell, entry, notes, false);
    return next;
  }
  return undo.length === state.undo.length ? state : { ...state, undo };
}

/** The digits that do not clash with any shown digit among a cell's peers. */
export function candidatesOf(board: SudokuBoard, state: SudokuState, cell: number): number {
  const g = geometry(board.size);
  let used = 0;
  for (const p of g.peers[cell] ?? []) {
    const d = shownDigit(board, state, p);
    if (d !== 0) used |= 1 << (d - 1);
  }
  return g.allDigits & ~used;
}

/**
 * The cell a hint should fill when nothing usable is selected: in a "check" game the first wrong digit
 * (reading order), else the empty cell with the fewest candidates (the first of equals) — null when
 * none. A "free" game's hint NEVER tells right from wrong: it only ever fills an EMPTY cell.
 */
export function pickHintCell(board: SudokuBoard, state: SudokuState): number | null {
  if (state.mode === "check") {
    for (let i = 0; i < board.puzzle.length; i++) {
      if (!isLocked(board, state, i) && isWrong(board, state, i)) return i;
    }
  }
  let best: number | null = null;
  let bestCount = Number.POSITIVE_INFINITY;
  for (let i = 0; i < board.puzzle.length; i++) {
    if (shownDigit(board, state, i) !== 0) continue;
    const count = popcount(candidatesOf(board, state, i));
    if (count < bestCount) {
      best = i;
      bestCount = count;
    }
  }
  return best;
}

/** A hint can be used now: one is left — and, in a "free" game, some cell is still empty (a hint
 *  there fills empty cells only). */
export function hintAvailable(board: SudokuBoard, state: SudokuState): boolean {
  if (hintsLeft(state) === 0) return false;
  if (state.mode === "check") return true;
  for (let i = 0; i < board.puzzle.length; i++) if (shownDigit(board, state, i) === 0) return true;
  return false;
}

/**
 * Use a hint: fill the selected cell with its answer when it is empty or (in a "check" game) wrong,
 * else a cell {@link pickHintCell} chooses. In a "free" game a filled cell is never usable, right or
 * wrong — a hint there must not tell which (the operator, 2026-11-04). `cell` is the one filled —
 * null when no hint is left or nothing takes one (the state comes back unchanged).
 */
export function useHint(
  board: SudokuBoard,
  state: SudokuState,
  selected: number | null,
): { state: SudokuState; cell: number | null } {
  if (hintsLeft(state) === 0) return { state, cell: null };
  const entry = selected === null ? 0 : digitAt(state.entries, selected);
  const usable =
    selected !== null &&
    inRange(board, selected) &&
    !isLocked(board, state, selected) &&
    (state.mode === "free" ? entry === 0 : entry !== digitAt(board.solution, selected));
  const cell = usable ? selected : pickHintCell(board, state);
  if (cell === null) return { state, cell: null };
  const filled = withCell(state, cell, digitAt(board.solution, cell), 0, false);
  return { state: { ...filled, hinted: [...state.hinted, cell] }, cell };
}

/**
 * Is `state` a well-formed game for `board`? Its shape (lengths, digit and note ranges, the undo
 * history), no entry on a given, at most {@link SUDOKU_HINTS} distinct hinted cells, each holding its
 * answer and none a given; a known mode, mistakes 0–{@link SUDOKU_MAX_MISTAKES} in "check" and none in
 * "free"; the full-grid message and «نشانم بده» only in "free" (the second only after the first); the
 * marked cells distinct, each holding a wrong digit of the player's. The api runs it on every save;
 * the phone on a state it adopts.
 */
export function validateState(board: SudokuBoard, state: SudokuState): boolean {
  const { size } = board;
  const cells = size * size;
  if (!isGridShape(state.entries, size)) return false;
  if (state.notes.length !== cells) return false;
  const allDigits = (1 << size) - 1;
  for (let i = 0; i < cells; i++) {
    const n = state.notes[i];
    if (n === undefined || !Number.isInteger(n) || n < 0 || n > allDigits) return false;
    if (isGiven(board, i) && (digitAt(state.entries, i) !== 0 || n !== 0)) return false;
    if (digitAt(state.entries, i) !== 0 && n !== 0) return false;
  }
  if (state.hinted.length > SUDOKU_HINTS || new Set(state.hinted).size !== state.hinted.length) {
    return false;
  }
  for (const h of state.hinted) {
    if (!inRange(board, h) || isGiven(board, h)) return false;
    if (digitAt(state.entries, h) !== digitAt(board.solution, h)) return false;
  }
  if (!Number.isFinite(state.elapsedMs) || state.elapsedMs < 0) return false;
  if (!(SUDOKU_MODES as readonly string[]).includes(state.mode)) return false;
  const { mistakes, fullShown, revealed, marked } = state;
  if (!Number.isInteger(mistakes) || mistakes < 0 || mistakes > SUDOKU_MAX_MISTAKES) return false;
  if (typeof fullShown !== "boolean" || typeof revealed !== "boolean") return false;
  if (state.mode === "check" ? fullShown || revealed : mistakes !== 0) return false;
  if (revealed && !fullShown) return false;
  if (!Array.isArray(marked) || new Set(marked).size !== marked.length) return false;
  if (marked.length > 0 && !revealed) return false;
  for (const m of marked) {
    if (!inRange(board, m) || isLocked(board, state, m) || !isWrong(board, state, m)) return false;
  }
  if (state.undo.length > SUDOKU_UNDO_LIMIT) return false;
  for (const step of state.undo) {
    const [cell, entry, notes] = step;
    if (!inRange(board, cell) || !Number.isInteger(entry) || entry < 0 || entry > size)
      return false;
    if (!Number.isInteger(notes) || notes < 0 || notes > allDigits) return false;
  }
  return true;
}
